import React, { useEffect, useRef, useMemo } from 'react';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ChartData } from '@rti/shared';
import 'chartjs-adapter-date-fns';
import { getRsColors } from '@/utils/chartColors';
import { useTheme } from '@/contexts/ThemeContext';
import { normalizeSeries } from '@/utils/normalizeChartData';

Chart.register(...registerables, ChartDataLabels);

interface PriceHistoryChartProps {
  chartData: ChartData | null;
}

const PriceHistoryChart: React.FC<PriceHistoryChartProps> = ({ chartData }) => {
  const { isDark } = useTheme();
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const creatingRef = useRef(false);
  const hasAnimatedRef = useRef(false);

  const rsColors = useMemo(() => getRsColors(), []);
  // Axis/grid/tooltip colors must derive from the reactive `isDark` value, NOT from
  // getRsColors() (which reads CSS variables once at mount and goes stale on theme change).
  const tickColor = isDark ? '#e9ede6' : '#1e2019';
  const gridColor = isDark ? '#566151' : '#d0d3ce';
  const tooltipBg = isDark ? '#2a2e28' : '#f0f2ee';
  const historySeries = useMemo(() => normalizeSeries(chartData?.history), [chartData]);

  const xScaleType = useMemo(() => {
    const sample = historySeries[0]?.x;
    if (!sample) return 'linear';
    if (sample instanceof Date) return 'time';
    if (typeof sample === 'string') {
      if (/\d{4}-\d{2}-\d{2}/.test(sample) || !isNaN(Date.parse(sample))) return 'time';
      return 'category';
    }
    if (typeof sample === 'number') {
      if (sample > 10 ** 11) return 'time';
      return 'linear';
    }
    return 'linear';
  }, [historySeries]);

  useEffect(() => {
    // No data => cleanup
    if (!chartRef.current || !historySeries.length) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
      return;
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    if (creatingRef.current) return;
    creatingRef.current = true;

    if (chartInstance.current) {
      try { chartInstance.current.destroy(); } catch { /* ignore */ }
      chartInstance.current = null;
    }

    // Convert data to proper Chart.js format
    const convertedData = historySeries.map(p => {
      let xValue = p.x;
      if (xScaleType === 'time') {
        // Parse the date and normalize to first of month in UTC timezone
        const date = new Date(p.x);
        // Create new date object for first of month using UTC to avoid timezone issues
        xValue = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
      }
      return {
        x: xValue,
        y: Number(p.y)
      };
    });

    const config: ChartConfiguration = {
      type: 'line',
      data: {
        datasets: [{
          label: 'Price History',
          data: convertedData,
          borderColor: rsColors.darkGreen,
          backgroundColor: rsColors.darkGreen,
          fill: false,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
        },
        parsing: {
          xAxisKey: 'x',
          yAxisKey: 'y'
        },
        animation: hasAnimatedRef.current ? false : {
          duration: 1200,
          easing: 'easeOutQuart',
        },
        animations: hasAnimatedRef.current ? {} : {
          x: {
            type: 'number',
            duration: 1200,
            easing: 'easeOutQuart',
            from: NaN,
            delay: (ctx: any) => ctx.type === 'data' ? ctx.dataIndex * 25 : 0
          },
          y: {
            type: 'number',
            duration: 1200,
            easing: 'easeOutCubic',
            from: (ctx: any) => {
              if (ctx.type !== 'data') return undefined;
              const yScale = ctx.chart.scales.y;
              return yScale.getPixelForValue ? yScale.min : undefined;
            },
            delay: (ctx: any) => ctx.type === 'data' ? ctx.dataIndex * 25 : 0
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: tickColor,
              usePointStyle: true,
              pointStyle: 'line',
              font: { size: 13.7 },
            },
          },
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            backgroundColor: tooltipBg,
            titleColor: tickColor,
            bodyColor: tickColor,
            borderColor: gridColor,
            usePointStyle: true,
            borderWidth: 1,
            callbacks: {
              label: (ctx) => {
                const y = ctx.parsed.y;
                return `${ctx.dataset.label}: ${typeof y === 'number' ? y.toFixed(2) : y}`;
              }
            }
          },
          datalabels: {
            anchor: (ctx: any) => ctx.dataIndex % 2 === 0 ? 'end' : 'start',
            align: (ctx: any) => ctx.dataIndex % 2 === 0 ? 'top' : 'bottom',
            color: rsColors.darkGreen,
            formatter: (value) => value.y.toFixed(2),
            font: {
              size: 13.7
            }
          }
        },
        scales: {
          x: xScaleType === 'time' ? {
            type: 'time',
            time: {
              unit: 'month',
              displayFormats: { month: 'MMM yy' },
              parser: 'YYYY-MM-DD'
            },
            grid: { color: gridColor },
            ticks: { color: tickColor },
            min: convertedData.length > 0 ? new Date(convertedData[0].x).getTime() : undefined,
            max: (() => {
              if (convertedData.length === 0) return undefined;
              const lastDate = new Date(convertedData[convertedData.length - 1].x).getTime();
              return lastDate + (10 * 24 * 60 * 60 * 1000);
            })()
          } : xScaleType === 'category' ? {
            type: 'category',
            grid: { color: gridColor },
            ticks: { color: tickColor }
          } : {
            type: 'linear',
            grid: { color: gridColor },
            ticks: { color: tickColor }
          },
          y: {
            grid: { color: gridColor },
            ticks: {
              color: tickColor,
              callback: (v: any) => Number(v).toFixed(2)
            },
            grace: '5%'
          }
        },
        interaction: { mode: 'nearest', axis: 'x', intersect: false },
      }
    };

    try {
      chartInstance.current = new Chart(ctx, config);
      if (!hasAnimatedRef.current) hasAnimatedRef.current = true;
    } catch (error) {
      console.error('❌ PriceHistoryChart - Error creating chart:', error);
    }

    creatingRef.current = false;

    return () => {
      if (chartInstance.current) {
        try { chartInstance.current.destroy(); } catch { /* ignore */ }
        chartInstance.current = null;
      }
      creatingRef.current = false;
    };
  }, [historySeries, rsColors, xScaleType, isDark, tickColor, gridColor, tooltipBg]);

  return historySeries.length ? (
    <div className="w-full h-full relative">
      <canvas ref={chartRef} className="w-full h-full" />
    </div>
  ) : (
    <div className="text-xs text-gray-400 p-2">No price history data</div>
  );
};

export default PriceHistoryChart;
